import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { List, WindowScroller } from "react-virtualized";

import { currentUserVar, UserPoolClient, useQuery, useReactiveVar } from "@apollo";
import { USER_POOL_GET_USER_OFFERS } from "@queries";

// Misc Imports
import { useMediaQuery, useWindowDimensions } from "@hooks";
import { Offer } from "@models";
import { makeStyles } from "@styles";
import {
  createRedirectData,
  isServerSideRendering,
  ModalHelper,
  OfferCardHelpers,
  openOfferRedirect,
} from "@utils";

// Component imports
import {
  DividerLine,
  GridCardView,
  GridCardRowContainer,
  ListCardView,
  ListCardRowContainer,
} from "@components";
import { sendSegmentTrackEvent } from "helpers/analytics";

type Props = {
  display: "Cards" | "List";
  offers: StaticOffer[];
};

const useStyles = makeStyles(() => ({
  list: {
    overflow: "visible !important",
    "& .ReactVirtualized__Grid__innerScrollContainer": {
      overflow: "visible !important",
    },
  },
}));

export const OfferCardsContainer: React.NamedExoticComponent<Props> = memo(
  function OfferCardsContainerComponent({ display, offers }: Props) {
    if (isServerSideRendering()) {
      return <></>;
    }
    const classes: any = useStyles({});
    const user = useReactiveVar(currentUserVar);

    const small: boolean = useMediaQuery("(max-width:959px)");

    const { data } = useQuery<{ userOffers: Offer[] }>(USER_POOL_GET_USER_OFFERS, {
      client: UserPoolClient,
      fetchPolicy: "cache-only",
    });

    const userOfferIds = data?.userOffers?.map((offer) => offer.id) || undefined;

    const onRedirectClicked = useCallback(
      async (offer: StaticOffer): Promise<void> => {
        try {
          const redirectData = await createRedirectData({ offer });
          sendSegmentTrackEvent("shopAndEarnButton", {
            advertiserName: offer.advertiserName,
            affiliateNetwork: offer.affiliateNetwork,
            trackingUrl: redirectData.redirectUrl,
          });
          openOfferRedirect(redirectData);
        } catch (err) {
          console.error(err);
        }
      },
      [user],
    );

    const onTermsModalClicked = useCallback((offer: StaticOffer): void => {
      const termsModalProps: TermsData = {
        name: offer.advertiserName,
        terms: offer.terms,
        offer,
        offerId: offer.id,
        openOfferRedirect,
      };

      ModalHelper.open({ modalType: "terms", modalProps: termsModalProps });
    }, []);

    const onSignUpModalClicked = useCallback(
      (offer: StaticOffer): void => {
        if (small) {
          ModalHelper.open({ modalType: "signUpDrawer" });
        } else {
          const signUpModalProps: SignUpData = {
            name: offer.advertiserName,
            logo: offer.advertiserLogoImage,
          };
          ModalHelper.open({
            modalType: "signUp",
            modalProps: signUpModalProps,
          });
        }
      },
      [small],
    );

    // React Virtualized related variables
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [measuredContentWidth, setMeasuredContentWidth] = useState<number>(0);
    const { width, height } = useWindowDimensions();
    const fallbackContentWidth: number = Math.min(
      width <= 1279 ? width * 0.9 : width * 0.8,
      1400,
    );
    const contentWidth: number = measuredContentWidth || fallbackContentWidth;
    const rowGap = 17;
    const gridBottomClearance = 16;
    const cardListHeight = 80 + rowGap;
    const cardGridHeight: number = 254 + rowGap + gridBottomClearance;
    const rowOverScanCount = 4;
    const rowCount: number =
      display === "List"
        ? offers.length
        : OfferCardHelpers.getRowsAmount(offers.length, contentWidth);
    const maxItemsPerRow: number =
      display === "List" ? 1 : OfferCardHelpers.getMaxItemsAmountPerRow(contentWidth);

    useEffect(() => {
      const updateContentWidth = (): void => {
        if (!containerRef.current) {
          return;
        }

        setMeasuredContentWidth(containerRef.current.getBoundingClientRect().width);
      };

      updateContentWidth();
      window.addEventListener("resize", updateContentWidth);

      return (): void => window.removeEventListener("resize", updateContentWidth);
    }, []);

    // Renders one row of cards to be rendered by React Virtualized.
    // In Cards mode, number of cards per row is calculated based on content width.
    // In List view, one row is one card.
    const OfferCardRow = ({ index, style }): JSX.Element => {
      const offersForRow: StaticOffer[] = OfferCardHelpers.generateIndexesForRow(
        index,
        maxItemsPerRow,
        offers.length,
      ).map((offerIndex: number) => offers[offerIndex]);

      const lastIndex: number = offers.length - 1;

      const mapOfferCardComponentsToRow = (offer: StaticOffer): JSX.Element => {
        const favourited: boolean = userOfferIds?.includes(offer.id);

        if (display === "Cards") {
          return (
            <React.Fragment key={offer.id}>
              <GridCardView
                offer={offer}
                favourited={favourited}
                loggedIn={!!user}
                mobile={!!small}
                onRedirectClicked={onRedirectClicked}
                onTermsModalClicked={onTermsModalClicked}
                onSignUpModalClicked={onSignUpModalClicked}
              />
            </React.Fragment>
          );
        }

        if (display === "List") {
          return (
            <React.Fragment key={offer.id}>
              <ListCardView
                offer={offer}
                favourited={favourited}
                loggedIn={!!user}
                onRedirectClicked={onRedirectClicked}
                onTermsModalClicked={onTermsModalClicked}
                onSignUpModalClicked={onSignUpModalClicked}
              />
              {index !== lastIndex && <DividerLine />}
            </React.Fragment>
          );
        }
      };

      return display === "List" ? (
        <ListCardRowContainer key={`offers-cards-row-${index + 1}`} style={style}>
          {offersForRow.map(mapOfferCardComponentsToRow)}
        </ListCardRowContainer>
      ) : (
        <GridCardRowContainer
          key={`offers-cards-row-${index + 1}`}
          maxItemsPerRow={maxItemsPerRow}
          style={style}
        >
          {offersForRow.map(mapOfferCardComponentsToRow)}
        </GridCardRowContainer>
      );
    };

    return (
      <div ref={containerRef}>
        <WindowScroller>
          {({ scrollTop }) => (
            <List
              autoHeight
              containerStyle={{ overflow: "visible" }}
              height={height}
              rowCount={rowCount}
              rowHeight={display === "List" ? cardListHeight : cardGridHeight}
              rowRenderer={OfferCardRow}
              overscanRowCount={rowOverScanCount}
              scrollTop={scrollTop}
              width={contentWidth}
              className={classes.list}
              style={{ outline: "none", width: "100%", overflow: "visible" }}
            />
          )}
        </WindowScroller>
      </div>
    );
  },
);
